import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import Seo from "@/components/feature/Seo";
import PageBreadcrumb from "@/components/feature/PageBreadcrumb";
import SubscribeForm from "@/components/feature/SubscribeForm";
import ComicCard from "@/components/feature/ComicCard";
import { works, formatViews } from "@/mocks/comics";

const STATUS_STYLE: Record<string, string> = {
  连载中: "bg-primary-100 text-primary-700",
  已完结: "bg-secondary-100 text-secondary-700",
  不定期更新: "bg-accent-100 text-accent-700",
};

export default function WorkDetail() {
  const { id } = useParams<{ id: string }>();
  const work = works.find((w) => w.id === id);
  const [showAll, setShowAll] = useState(false);

  if (!work) {
    return (
      <Layout>
        <div className="mx-auto flex max-w-3xl flex-col items-center px-4 pb-24 pt-40 text-center md:px-6">
          <i className="ri-file-search-line text-5xl text-foreground-300" aria-hidden="true" />
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">作品不存在</h1>
          <p className="mt-2 text-foreground-500">你访问的作品可能已被下架或链接有误。</p>
          <Link to="/works" className="mt-6 rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 hover:bg-primary-600">
            返回作品列表
          </Link>
        </div>
      </Layout>
    );
  }

  const visibleChapters = showAll ? work.chapters : work.chapters.slice(0, 10);
  const related = works.filter((w) => w.id !== work.id && w.category === work.category).slice(0, 4);
  const fallbackRelated = related.length > 0 ? related : works.filter((w) => w.id !== work.id).slice(0, 4);

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "/" },
          { "@type": "ListItem", position: 2, name: "作品列表", item: "/works" },
          { "@type": "ListItem", position: 3, name: work.title },
        ],
      },
      {
        "@type": "Book",
        name: work.title,
        author: { "@type": "Person", name: work.author },
        inLanguage: "zh-CN",
        description: work.description,
      },
    ],
  };

  const goSubscribe = () =>
    document.getElementById("subscribe")?.scrollIntoView({ behavior: "smooth" });

  return (
    <Layout>
      <Seo
        title={`${work.title} - 最新章节与连载更新 | 漫蛙漫画网`}
        description={`${work.title}最新连载更新于${work.lastUpdated}，最新章节「${work.lastChapter}」。${work.description}开启追更提醒，第一时间获知更新动态。`}
        keywords={`${work.title},${work.category}漫画,最新章节,连载更新`}
        jsonLd={jsonLd}
      />

      <div className="bg-background-100">
        <div className="mx-auto max-w-7xl px-4 pb-10 pt-24 md:px-6 md:pt-28">
          <PageBreadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: "作品列表", to: "/works" },
              { label: work.title },
            ]}
          />

          <div className="mt-6 grid grid-cols-1 gap-8 md:grid-cols-[300px_1fr]">
            <div className="mx-auto w-full max-w-[300px]">
              <div className="relative aspect-[3/4] w-full overflow-hidden rounded-2xl ring-1 ring-background-200">
                <img
                  src={work.cover}
                  alt={`${work.title} 漫画封面`}
                  title={`${work.title} 连载漫画`}
                  className="h-full w-full object-cover object-top"
                />
                <span className="absolute left-3 top-3 rounded-full bg-background-50/90 px-2.5 py-1 text-xs font-medium text-foreground-700">
                  {work.category}
                </span>
              </div>
            </div>

            <div>
              <h1 className="font-heading text-2xl font-extrabold tracking-tight text-foreground-950 md:text-3xl">
                {work.title}
              </h1>

              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span className={`rounded-full px-3 py-1 text-xs font-semibold ${STATUS_STYLE[work.status]}`}>
                  {work.status}
                </span>
                <span className="flex items-center gap-1 rounded-full bg-accent-100 px-3 py-1 text-xs font-semibold text-accent-700">
                  <i className="ri-star-fill" aria-hidden="true" />
                  {work.rating.toFixed(1)}
                </span>
                <span className="rounded-full bg-background-200 px-3 py-1 text-xs font-medium text-foreground-600">
                  {formatViews(work.subscribers)} 人追更
                </span>
              </div>

              <dl className="mt-5 grid grid-cols-2 gap-4 text-sm sm:grid-cols-4">
                <div>
                  <dt className="text-foreground-500">作者</dt>
                  <dd className="mt-1 font-medium text-primary-700">{work.author}</dd>
                </div>
                <div>
                  <dt className="text-foreground-500">最后更新</dt>
                  <dd className="mt-1 font-medium text-foreground-900">{work.lastUpdated}</dd>
                </div>
                <div>
                  <dt className="text-foreground-500">最新章节</dt>
                  <dd className="mt-1 font-medium text-foreground-900">{work.lastChapter}</dd>
                </div>
                <div>
                  <dt className="text-foreground-500">总话数</dt>
                  <dd className="mt-1 font-medium text-foreground-900">{work.chapters.length} 话</dd>
                </div>
              </dl>

              <p className="mt-5 text-sm leading-relaxed text-foreground-700">{work.description}</p>

              <div className="mt-5 flex flex-wrap gap-1.5">
                {work.tags.map((t) => (
                  <span key={t} className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-800">
                    #{t}
                  </span>
                ))}
              </div>

              <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                <button
                  type="button"
                  onClick={goSubscribe}
                  className="whitespace-nowrap rounded-full bg-primary-500 px-7 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-notification-3-line mr-1.5" aria-hidden="true" />
                  追更提醒
                </button>
                <button
                  type="button"
                  onClick={() => document.getElementById("chapter-list")?.scrollIntoView({ behavior: "smooth" })}
                  className="whitespace-nowrap rounded-full bg-background-50 px-7 py-3 text-sm font-semibold text-primary-700 ring-1 ring-primary-200 transition-colors hover:bg-primary-100"
                >
                  查看章节目录
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <section id="chapter-list" className="scroll-mt-16 bg-background-50 py-12 md:py-16">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <h2 className="font-heading text-xl font-extrabold text-foreground-950 md:text-2xl">
            章节目录
          </h2>
          <p className="mt-2 text-sm text-foreground-500">共 {work.chapters.length} 话 · 按更新时间倒序排列</p>

          <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {visibleChapters.map((c) => (
              <div
                key={c.no}
                className="flex items-center justify-between gap-3 rounded-xl bg-background-100 px-5 py-3.5 ring-1 ring-background-200 transition-colors hover:ring-primary-300"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-xs font-bold text-primary-700">
                    {c.no}
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-foreground-900">{c.title}</p>
                    <p className="text-xs text-foreground-500">{c.updatedAt}</p>
                  </div>
                </div>
                <span className="flex shrink-0 items-center gap-1 text-xs text-foreground-500">
                  <i className="ri-eye-line" aria-hidden="true" />
                  {formatViews(c.views)}
                </span>
              </div>
            ))}
          </div>

          {work.chapters.length > 10 && (
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-background-100 py-3 text-sm font-semibold text-primary-700 ring-1 ring-background-200 transition-colors hover:bg-primary-100"
            >
              {showAll ? "收起章节目录" : `展开全部 ${work.chapters.length} 话`}
              <i className={`ri-arrow-down-s-line transition-transform ${showAll ? "rotate-180" : ""}`} aria-hidden="true" />
            </button>
          )}
        </div>
      </section>

      <section id="subscribe" className="scroll-mt-16 bg-gradient-to-br from-primary-700 via-primary-800 to-secondary-900 py-14 md:py-20">
        <div className="mx-auto max-w-4xl px-4 text-center md:px-6">
          <h2 className="font-heading text-2xl font-extrabold text-background-50 md:text-3xl">
            追更《{work.title}》，最新章节不再错过
          </h2>
          <p className="mt-3 text-sm text-background-100/85">
            留下邮箱，更新第一时间推送给你。完全免费，随时可退订。
          </p>
          <div className="mt-8">
            <SubscribeForm />
          </div>
        </div>
      </section>

      <section className="bg-background-100 py-12 md:py-16">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <h2 className="font-heading text-xl font-extrabold text-foreground-950 md:text-2xl">
            相关推荐
          </h2>
          <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
            {fallbackRelated.map((w) => (
              <ComicCard key={w.id} work={w} />
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
}